<template>
  <div class="tool-questions">
    <template v-if="ui.answers.finalising">
      <div class="d-flex flex-column justify-content-center align-items-center">
        <loading-spinner size="4x"
                         class="m-4"></loading-spinner>
        <p>Saving your survey results, please wait...</p>
      </div>
    </template>
    <template v-else>
      <div class="tool-questions__question__container">
        <div class="tool-questions__indicator">
          <div class="tool-questions__indicator__count">
            Question {{ activeQuestionNumber }} of {{ mutableQuestions.length }}
          </div>
          <div class="tool-questions__indicator__segments">
            <div
              v-for="(value, index) in mutableQuestions"
              :key="index"
              class="tool-questions__indicator__segments__segment"
              :class="{
                'tool-questions__indicator__segments__segment--complete': index <= activeIndex,
              }"></div>
          </div>
        </div>
        <div class="tool-questions__question">
          <transition name="fade">
            <h2 v-if="ui.answers.saving === false">
              {{ activeQuestion[questionKey] }}
            </h2>
          </transition>
        </div>
        <template v-if="ui.answers.saving">
          <div class="tool-questions__slider__container m-auto">
            <loading-spinner size="2x"></loading-spinner>
          </div>
        </template>
        <template v-else>
          <div class="tool-questions__slider__container">
            <input
              id="ui-tool-question-bar-slider"
              ref="questionBarSlider"
              v-model="sliderValue"
              class="tool-questions__slider"
              type="range"
              min="ui.slider.min"
              max="ui.slider.max"
              @mouseup="onSliderValueSet"
              @touchend="onSliderValueSet">
            <div class="tool-questions__slider__indicator">
              <div v-for="(value, index) in ui.slider.indicators"
                   :key="index">
                {{ value }}
              </div>
            </div>
          </div>
        </template>
      </div>
      <div class="tool-questions__navigation mb-3">
        <button
          class="cta cta--secondary"
          :class="{ 'd-none': ui.previous.hidden && activeIndex === 0 }"
          @click="onPrevious">
          <font-awesome-icon icon="chevron-left"
                             size="xs"></font-awesome-icon>
          {{ ui.previous.label }}
        </button>
        <transition name="fade">
          <template v-if="ui.slider.touched">
            <button class="cta cta--primary--alt"
                    :disabled="ui.answers.saving"
                    @click="onNext">
              <template v-if="isLastQuestion">
                <font-awesome-icon :icon="['fal', 'save']"
                                   size="1x"></font-awesome-icon>
                Save
              </template>
              <template v-else>
                Next
                <font-awesome-icon icon="chevron-right"
                                   size="xs"></font-awesome-icon>
              </template>
            </button>
          </template>
          <template v-else>
            <div class="cta tool-questions__navigation__dummy">
              Move slider to progress
            </div>
          </template>
        </transition>
      </div>
    </template>
  </div>
</template>
<script>
import { INSTANCE_TYPE } from "~/Components/constants";
import { utils } from "~/Scripts/vue/mixins/common";

import {
  listQuestions as listThreeSixtyQuestions,
  saveAnswers as saveThreeSixtyAnswers,
} from "~/Scripts/resources/threeSixtyResource";

import {
  listQuestions as listEngageQuestions,
  saveAnswers as saveEngageAnswers,
} from "~/Scripts/resources/engageResource";

import {
  listQuestions as listStyleQuestions,
  saveAnswers as saveStyleAnswers,
} from "~/Scripts/resources/styleResource";

import {
  listQuestions as listSurveyQuestions,
  saveResponse,
} from "~/Scripts/resources/surveyResource";

export default {
  name: "ToolQuestions",
  mixins: [utils],
  props: {
    id: {
      type: String,
      default: null,
    },
    reviewer: {
      type: String,
      default: null,
    },
    idx: {
      type: Number,
      default: 0,
    },
    retakeSurvey: {
      type: Boolean,
      default: false,
    },
    surveyQuestions: {
      type: Array,
      default: () => null,
      validator: val => {
        const [question] = val;
        console.log( question );

        const threeSixtyKeys = [
          "answer",
          "question",
          "maxAnswerLength",
          "questionId",
          "questionType",
          "questionTypeSystemCode",
        ];
        const engageKeys = [
          "question",
          "questionId",
          "answer",
          "questionType",
          "questionTypeSystemCode",
        ];
        const styleKeys = ["questionId", "questionText", "answer"];
        const surveyKeys = ["questionText", "answer", "surveyQuestionId"];
        return (
          threeSixtyKeys.every( prop => prop in question )
          || styleKeys.every( prop => prop in question )
          || surveyKeys.every( prop => prop in question )
          || engageKeys.every( prop => prop in question )
        );
      },
    },
    asReviewer: {
      type: Boolean,
      default: false,
    },
    next: {
      type: String,
      required: true,
    },
    instanceType: {
      type: String,
      required: true,
      validator: val => [
        INSTANCE_TYPE.THREE_SIXTY,
        INSTANCE_TYPE.PERSONA,
        INSTANCE_TYPE.STYLE,
        INSTANCE_TYPE.SURVEY,
        INSTANCE_TYPE.ENGAGE,
      ].includes( val.toUpperCase() ),
    },
  },
  data: () => ( {
    instanceId: null,
    reviewerId: null,
    retake: false,
    sliderValue: 50,
    activeIndex: 0,
    questions: [],
    mutableQuestions: [],
    INSTANCE_TYPE,
    ui: {
      previous: {
        label: "Back",
        hidden: ( window.history && window.history.length > 1 ) === false,
      },
      slider: {
        min: 0,
        max: 100,
        indicators: ["Never", "Sometimes", "Always"],
        touched: false,
      },
      questions: {
        loading: false,
        error: false,
        errorMessage: "Failed to load questions",
      },
      answers: {
        finalising: false,
        saving: false,
        error: false,
        errorMessage: "Failed to save answers",
      },
    },
    hasHistory: window.history.length > 1,
    surveyResponseId: JSON.parse( null ),
  } ),
  computed: {
    activeQuestion () {
      return this.mutableQuestions[this.activeIndex] || { question: null };
    },
    activeQuestionNumber () {
      return this.activeIndex + 1;
    },
    isLastQuestion () {
      return this.activeQuestionNumber === this.mutableQuestions.length;
    },
    sliderTextValue () {
      if ( this.sliderValue >= 0 && this.sliderValue <= 33 ) {
        return "Never";
      }
      if ( this.sliderValue >= 66 && this.sliderValue <= 100 ) {
        return "Always";
      }
      return "Sometimes";
    },
    surveyAnswerSystemCode () {
      if ( this.sliderValue >= 0 && this.sliderValue < 20 ) {
        return "Strongly_Disagree";
      }
      if ( this.sliderValue >= 20 && this.sliderValue < 40 ) {
        return "Disagree";
      }
      if ( this.sliderValue >= 40 && this.sliderValue < 60 ) {
        return "Cant_Comment";
      }
      if ( this.sliderValue >= 60 && this.sliderValue < 80 ) {
        return "Agree";
      }
      if ( this.sliderValue >= 80 && this.sliderValue <= 100 ) {
        return "Strongly_Agree";
      }
      return "Cant_Comment";
    },
    questionKey () {
      if ( this.instanceType === this.INSTANCE_TYPE.THREE_SIXTY ) {
        return "question";
      }
      if ( this.instanceType === this.INSTANCE_TYPE.STYLE ) {
        return "questionText";
      }
      if ( this.instanceType === this.INSTANCE_TYPE.SURVEY ) {
        return "questionText";
      }
      if ( this.instanceType === this.INSTANCE_TYPE.ENGAGE ) {
        return "question";
      }
      return undefined;
    },
    saveAnswers () {
      if ( this.instanceType === this.INSTANCE_TYPE.THREE_SIXTY ) {
        return this.saveThreeSixtyAnswer;
      }
      if ( this.instanceType === this.INSTANCE_TYPE.STYLE ) {
        return this.saveStyleAnswer;
      }
      if ( this.instanceType === this.INSTANCE_TYPE.SURVEY ) {
        return this.saveSurveyAnswer;
      }
      if ( this.instanceType === this.INSTANCE_TYPE.ENGAGE ) {
        return this.saveEngageAnswer;
      }

      return this.redirectToError;
    },
    loadQuestions () {
      if ( this.instanceType === this.INSTANCE_TYPE.THREE_SIXTY ) {
        return this.loadThreeSixtyQuestions;
      }
      if ( this.instanceType === this.INSTANCE_TYPE.STYLE ) {
        return this.loadStyleQuestions;
      }
      if ( this.instanceType === this.INSTANCE_TYPE.SURVEY ) {
        return this.loadSurveyQuestions;
      }
      if ( this.instanceType === this.INSTANCE_TYPE.ENGAGE ) {
        return this.loadEngageQuestions;
      }
      return this.redirectToError;
    },
  },
  watch: {
    activeQuestion ( question ) {
      if ( question.answer == null ) {
        this.resetSliderValue();
      } else {
        this.setExistingSliderValue();
      }
    },
  },
  created () {
    const params = this.getURLParams();
    this.activeIndex = parseInt( params.idx || this.idx, 10 );
    this.instanceId = params.id || this.id;
    this.reviewerId = params.reviewer || this.reviewer;
    this.retake = params.retake || this.rekateSurvey;

    if ( this.asReviewer && this.reviewerId == null ) {
      this.redirectToError();
    }

    if ( this.instanceType === this.INSTANCE_TYPE.SURVEY ) {
      this.ui.slider.indicators = [
        "Strongly Disagree",
        "Disagree",
        "Can't comment",
        "Agree",
        "Strongly Agree",
      ];
    }

    // if the questions haven't been provided attempt to load them
    if ( this.surveyQuestions ) {
      this.mutableQuestions = this.surveyQuestions;
      // console.log( this.mutableQuestions );
    } else if ( this.instanceId ) {
      this.loadQuestions();
    } else {
      this.redirectToError();
    }
  },
  methods: {
    onNext () {
      this.ui.answers.saving = true;
      this.ui.answers.finalising = JSON.parse( JSON.stringify( this.isLastQuestion ) );

      this.saveAnswers()
        .then( data => {
          this.$emit( "savedSuccess", data );
          switch ( this.instanceType ) {
            case INSTANCE_TYPE.THREE_SIXTY:
            case INSTANCE_TYPE.ENGAGE:
            case INSTANCE_TYPE.STYLE: {
              if ( this.isLastQuestion ) {
                window.location.href = this.asReviewer
                  ? `${this.next}?id=${this.instanceId}&reviewer=${this.reviewerId}`
                  : `${this.next}?id=${this.instanceId}`;
              } else {
                this.activeIndex += 1;
              }
              break;
            }
            case INSTANCE_TYPE.SURVEY: {
              this.surveyResponseId = data;

              if ( this.isLastQuestion ) {
                this.$emit( "survey-complete", this.surveyResponseId );
                // window.location.href = `${this.next}?id=${this.instanceId}`;
              } else {
                this.activeIndex += 1;
              }
              break;
            }
            default: {
              throw new new Error( "Instance Type not recognised." )();
            }
          }
        } )
        .catch( err => {
          console.error( err );
          this.ui.answers.error = true;
          this.ui.answers.saving = false;
          this.ui.answers.finalising = false;
        } )
        .finally( () => {
          // remain loading whilst redirecting page on last question unless an error occurs.
          if ( this.ui.answers.finalising === false ) {
            // for ui purposes
            const ref = this;
            setTimeout( () => {
              ref.ui.answers.saving = false;
            }, 300 );
          }
        } );
    },
    onPrevious () {
      this.ui.slider.touched = false;

      if ( this.activeIndex === 0 ) {
        window.history.back();
      } else {
        this.activeIndex -= 1;
      }
    },
    onSliderValueSet () {
      this.ui.slider.touched = true;
      this.setAnswer();
    },
    resetSliderValue () {
      this.ui.slider.touched = false;
      this.sliderValue = 50;
    },
    setAnswer () {
      this.mutableQuestions[this.activeIndex].answer = this.sliderValue;
    },
    setExistingSliderValue () {
      this.sliderValue = this.activeQuestion.answer || 50;
      this.ui.slider.touched = true;
    },
    saveThreeSixtyAnswer () {
      const { questionId, answer } = this.activeQuestion;

      const mappedAnswers = [
        {
          questionId,
          answer,
        },
      ];

      const payload = {
        instanceId: this.instanceId,
        answers: mappedAnswers,
        reviewerId: this.reviewerId,
      };

      return new Promise( ( resolve, reject ) => {
        saveThreeSixtyAnswers( payload )
          .then( response => {
            const { status, data } = response;

            if ( status === 200 && data ) {
              resolve( data );
            } else {
              reject( new Error( "Answers failed to save." ) );
            }
          } )
          .catch( err => {
            reject( err );
          } );
      } );
    },
    saveEngageAnswer () {
      this.ui.answers.saving = true;
      this.ui.answers.finalising = JSON.parse( JSON.stringify( this.isLastQuestion ) );

      const { questionId, answer } = this.activeQuestion;

      const mappedAnswers = [
        {
          questionId,
          answer,
        },
      ];

      const payload = {
        instanceId: this.instanceId,
        answers: mappedAnswers,
        reviewerId: this.reviewerId,
      };

      return new Promise( ( resolve, reject ) => {
        saveEngageAnswers( payload )
          .then( response => {
            const { status, data } = response;

            if ( status === 200 && data ) {
              resolve( data );
            } else {
              reject( new Error( "Answers failed to save." ) );
            }
          } )
          .catch( err => {
            reject( err );
          } );
      } );
    },
    saveStyleAnswer () {
      this.ui.answers.saving = true;
      this.ui.answers.finalising = JSON.parse( JSON.stringify( this.isLastQuestion ) );

      const { questionId, answer } = this.activeQuestion;

      const mappedAnswers = [
        {
          questionId,
          answer,
        },
      ];

      const payload = {
        instanceId: this.instanceId,
        answers: mappedAnswers,
        markAsComplete: this.isLastQuestion,
      };

      return new Promise( ( resolve, reject ) => {
        saveStyleAnswers( payload )
          .then( response => {
            const { status, data } = response;

            if ( status === 200 && data ) {
              resolve( data );
            } else {
              reject( new Error( "Answers failed to save." ) );
            }
          } )
          .catch( err => {
            reject( err );
          } );
      } );
    },
    saveSurveyAnswer () {
      this.ui.answers.saving = true;
      this.ui.answers.finalising = this.isLastQuestion;

      const { surveyQuestionId } = this.activeQuestion;

      const payload = {
        responseId: this.surveyResponseId,
        instanceId: this.instanceId,
        answerSystemCode: this.surveyAnswerSystemCode,
        surveyQuestionId,
      };

      return new Promise( ( resolve, reject ) => {
        saveResponse( payload )
          .then( response => {
            const { status, data } = response;

            if ( status === 200 && data ) {
              resolve( data );
            } else {
              reject( new Error( "Answers failed to save." ) );
            }
          } )
          .catch( err => {
            reject( err );
          } );
      } );
    },
    loadThreeSixtyQuestions () {
      this.ui.questions.loading = true;

      listThreeSixtyQuestions( this.instanceId, this.reviewerId )
        .then( response => {
          const { status, data } = response;

          if ( status === 200 && data ) {
            this.questions = data;
            this.mutableQuestions = [...data].filter( q => q.questionTypeSystemCode === "SLIDER" );
          } else {
            throw new Error( "Failed to load questions" );
          }
        } )
        .catch( ex => {
          console.error( ex );
          this.ui.questions.error = true;
        } )
        .finally( () => {
          this.ui.questions.loading = false;
        } );
    },
    loadEngageQuestions () {
      this.ui.questions.loading = true;

      listEngageQuestions( this.instanceId, this.reviewerId, "SLIDER" )
        .then( response => {
          const { status, data } = response;

          if ( status === 200 && data ) {
            this.questions = data;
            /*
            this.mutableQuestions = [...data].filter( q => q.questionTypeSystemCode === "SLIDER" );
            */
            this.mutableQuestions = data;
          } else {
            throw new Error( "Failed to load questions" );
          }
        } )
        .catch( ex => {
          console.error( ex );
          this.ui.questions.error = true;
        } )
        .finally( () => {
          this.ui.questions.loading = false;
        } );
    },
    loadStyleQuestions () {
      this.ui.questions.loading = true;

      listStyleQuestions( this.instanceId, this.reviewerId )
        .then( response => {
          const { status, data } = response;

          if ( status === 200 && data ) {
            this.questions = data;
            this.mutableQuestions = [...data];
          } else {
            throw new Error( "Failed to load questions" );
          }
        } )
        .catch( ex => {
          console.error( ex );
          this.ui.questions.error = true;
        } )
        .finally( () => {
          this.ui.questions.loading = false;
        } );
    },
    loadSurveyQuetions () {
      listSurveyQuestions( this.instanceId )
        .then( response => {
          const { status, data } = response;

          if ( status === 200 && data ) {
            this.questions = data;
            this.mutableQuestions = [...data];
          } else {
            throw new Error( "Failed to load questions" );
          }
        } )
        .catch( ex => {
          console.error( ex );
          this.ui.questions.error = true;
        } )
        .finally( () => {
          this.ui.questions.loading = false;
        } );
    },
    redirectToError () {
      window.location.href = "/error";
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../Styles/utils/index.less";

.fade-enter-active,
.fade-leave-active {
  transition-property: opacity;
  transition-duration: 0.4s;
  transition-timing-function: linear;
}

.fade-leave-active {
  display: none;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.tool-questions {
  &__indicator {
    display: flex;
    flex-flow: column;
    margin: 24px 0;

    &__count {
      color: @blue;
      font-size: 16px;
    }

    &__segments {
      display: flex;
      flex-wrap: wrap;
      gap: 2px;

      &__segment {
        height: 5px;
        flex: 1;
        min-width: 15px;
        background: @blue;
        .opacity(0.3);

        &--complete {
          .opacity(1);
        }

        @media (max-width: @lg) {
          flex: 0 1 auto;
        }
      }
    }
  }

  &__question {
    margin: 0 0 32px;

    &__container {
      min-height: 230px;
    }
  }

  &__slider {
    -webkit-appearance: none;
    width: 100%;
    height: 40px;
    outline: none;
    padding: 4px;
    .border-radius(48px);
    background: linear-gradient(to right, @mastered, @blue);
    border: 2px solid @blue;

    &__container {
      margin: 0 0 24px;
    }

    &__indicator {
      display: flex;
      justify-content: space-between;
    }

    .thumb() {
      -webkit-appearance: none;
      appearance: none;
      .circle(32px);
      background: @white;
      cursor: pointer;
    }

    &::-webkit-slider-thumb {
      .thumb();
    }

    &::-moz-range-thumb {
      .thumb();
    }

    &::ms-thumb {
      .thumb();
    }

    &__indicators {
      font-size: 0.8em;
    }
  }

  &__navigation {
    display: flex;
    justify-content: space-between;
    gap: 8px;

    &__dummy {
      background: #333;
      color: #fff;
      font-weight: 400;
      font-style: normal;
      font-size: 16px;
      padding: 8px 32px;
      cursor: not-allowed;

      @media (max-width: @sm) {
        padding: 8px;
      }
    }
  }
}
</style>
